import React from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { useDashboardTab, statusConfig } from "./useDashboardTab";
import { GmailConnectionCard } from "./GmailConnectionCard";

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, color }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 3,
      display: "flex",
      alignItems: "center",
      gap: 2,
      flex: 1,
      minWidth: 200,
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: `${color}15`,
        color: color,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={600} color={color}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

export const DashboardTab: React.FC = () => {
  const { activeFlows, stats, loading, formatDate } = useDashboardTab();

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Stats Cards */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <StatCard
          icon={<CheckCircleOutlineIcon />}
          label="Active Flows"
          value={stats.activeCount}
          color="#22c55e"
        />
        <StatCard
          icon={<PauseCircleOutlineIcon />}
          label="Inactive Flows"
          value={stats.inactiveCount}
          color="#6b7280"
        />
        <StatCard
          icon={<EditNoteIcon />}
          label="Draft Flows"
          value={stats.draftCount}
          color="#f59e0b"
        />
      </Box>

      {/* Gmail Connection */}
      <GmailConnectionCard />

      {/* Active Flows Table */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Active Flows
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Flows that are currently active and matching transactions
          </Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Flow Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : activeFlows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">
                      No active flows yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                activeFlows.map((flow) => {
                  const status = statusConfig[flow.status];
                  return (
                    <TableRow key={flow.identifier}>
                      <TableCell>
                        <Chip
                          label={status.label}
                          color={status.color}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{flow.name}</TableCell>
                      <TableCell>
                        {flow.description || (
                          <Typography color="warning.main" fontStyle="italic" variant="body2">
                            No description provided
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{flow.created_by}</TableCell>
                      <TableCell>{formatDate(flow.created_at)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};
